import { useEffect, useRef, useState } from 'react';
import { discountsAPI, getApiErrorMessage } from '@matix/api-client';
import type { VerifyOverrideResponse } from '@matix/types';
import { useLang } from '../../lib/language';

/**
 * Manager-override child modal.
 *
 * Opens above the DiscountModal when:
 *   - the cashier's discount exceeds permissions.max_receipt_discount_perc, OR
 *   - the chosen reason has requires_manager_override.
 *
 * The cashier types another authorized user's username + password.
 * Server checks credentials AND that user's discount permission via
 * POST /auth/verify-override (Phase 8a.1). On success, the parent
 * receives the manager_user_id which it stamps on the cart's discount.
 *
 * UX rules:
 *   - Username field auto-focused on open.
 *   - Enter on the password field submits.
 *   - Loading state disables both inputs + the button.
 *   - On INVALID_CREDENTIALS / SAME_USER / INSUFFICIENT_PERMISSION the
 *     server message is rendered inline; the password field is cleared
 *     so the cashier can retry without re-typing the username.
 *   - The modal does NOT auto-dismiss on Esc (parent handles its own
 *     overlay click) — the cashier explicitly cancels.
 */
export interface OverrideModalProps {
  /** The discount being authorized, expressed as a percent (always %). */
  requestedDiscountPerc: number;
  onSuccess: (args: { managerUserId: number; managerUsername: string }) => void;
  onClose: () => void;
}

export function OverrideModal(props: OverrideModalProps) {
  const { t } = useLang();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const usernameRef = useRef<HTMLInputElement>(null);

  // Auto-focus on mount.
  useEffect(() => {
    usernameRef.current?.focus();
  }, []);

  async function handleSubmit() {
    if (loading) return;
    if (!username.trim() || !password) {
      setError(t({ en: 'Username and password are required', ar: 'اسم المستخدم وكلمة المرور مطلوبان' }));
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result: VerifyOverrideResponse = await discountsAPI.verifyOverride({
        username: username.trim(),
        password,
        requested_discount_perc: props.requestedDiscountPerc,
        context: 'discount_override',
      });
      if (result.ok) {
        props.onSuccess({
          managerUserId: result.manager_user_id,
          managerUsername: result.manager_username,
        });
      } else {
        // Server returns a friendly message for each failure reason.
        // Just render it.
        setError(result.message);
        setPassword(''); // clear so they can retry without re-typing username
      }
    } catch (err) {
      setError(getApiErrorMessage(err) || t({ en: 'Could not verify credentials', ar: 'تعذّر التحقق من بيانات الاعتماد' }));
    } finally {
      setLoading(false);
    }
  }

  return (
    // Sits above the DiscountModal — higher z-index, semi-opaque overlay.
    <div
      className="modal-overlay show"
      style={{ zIndex: 1100, background: 'rgba(0, 0, 0, 0.55)' }}
      onClick={props.onClose}
    >
      <div
        className="modal"
        style={{ maxWidth: 400, width: '100%' }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label={t({ en: 'Manager override', ar: 'موافقة المدير' })}
      >
        <div className="modal-header">
          <div
            className="modal-header-img"
            style={{
              background: 'var(--warning-light, #faeeda)',
              color: 'var(--warning, #854f0b)',
            }}
          >
            ⚠
          </div>
          <div className="modal-header-title">
            <div className="modal-header-name">{t({ en: 'Manager approval', ar: 'موافقة المدير' })}</div>
            <div className="modal-header-name-ar">
              {t({
                en: `${props.requestedDiscountPerc.toFixed(1)}% discount needs another authorized user`,
                ar: `خصم ${props.requestedDiscountPerc.toFixed(1)}% يحتاج إلى مستخدم آخر مخوّل`,
              })}
            </div>
          </div>
          <button className="modal-close" onClick={props.onClose} aria-label={t({ en: 'Close', ar: 'إغلاق' })} disabled={loading}>
            ×
          </button>
        </div>

        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label style={labelStyle} htmlFor="override-username">{t({ en: 'Username', ar: 'اسم المستخدم' })}</label>
            <input
              id="override-username"
              ref={usernameRef}
              type="text"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={loading}
              style={inputStyle(!!error)}
              placeholder={t({ en: 'Manager username', ar: 'اسم مستخدم المدير' })}
            />
          </div>

          <div>
            <label style={labelStyle} htmlFor="override-password">{t({ en: 'Password', ar: 'كلمة المرور' })}</label>
            <input
              id="override-password"
              type="password"
              autoComplete="off"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void handleSubmit();
                }
              }}
              disabled={loading}
              style={inputStyle(!!error)}
              placeholder="••••••"
            />
          </div>

          {error && (
            <div
              style={{
                fontSize: 13,
                color: 'var(--danger, #791f1f)',
                background: 'var(--danger-light, #fcebeb)',
                border: '1px solid var(--danger, #a32d2d)',
                borderRadius: 'var(--radius, 8px)',
                padding: '8px 12px',
                display: 'flex',
                gap: 8,
                alignItems: 'flex-start',
              }}
            >
              <span style={{ fontWeight: 700 }}>!</span>
              <span style={{ flex: 1 }}>{error}</span>
            </div>
          )}
        </div>

        <div className="modal-footer" style={{ gap: 8 }}>
          <div style={{ flex: 1 }} />
          <button
            type="button"
            className="btn-secondary"
            onClick={props.onClose}
            disabled={loading}
          >
            {t({ en: 'Cancel', ar: 'إلغاء' })}
          </button>
          <button
            type="button"
            className="btn-primary-modal"
            onClick={handleSubmit}
            disabled={loading || !username.trim() || !password}
            style={{ background: 'var(--warning, #ba7517)' }}
          >
            {loading ? t({ en: 'Verifying…', ar: 'جارٍ التحقق…' }) : t({ en: 'Authorize', ar: 'اعتماد' })}
          </button>
        </div>
      </div>
    </div>
  );
}

const labelStyle: import('react').CSSProperties = {
  display: 'block',
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--text-muted)',
  marginBottom: 6,
};

function inputStyle(hasError: boolean): import('react').CSSProperties {
  return {
    width: '100%',
    height: 40,
    padding: '0 12px',
    fontSize: 14,
    border: `1px solid ${hasError ? 'var(--danger, #a32d2d)' : 'var(--border, #e2e6ec)'}`,
    borderRadius: 'var(--radius, 10px)',
    background: 'var(--surface, #fff)',
    color: 'var(--text)',
    outline: 'none',
  };
}
